import { call, put, all, takeEvery, race, take, select } from 'redux-saga/effects'

import {
  GET_LINE_ITEM,
  getLineItemSuccess,
  getLineItemFailure,
  GET_LINE_ITEMS,
  CLEAR_LINE_ITEMS,
  getLineItemsSuccess,
  getLineItemsFailure,
  CREATE_LINE_ITEM,
  createLineItemSuccess,
  createLineItemFailure,
  DELETE_LINE_ITEM,
  deleteLineItemSuccess,
  deleteLineItemFailure,
  UPDATE_LINE_ITEM,
  updateLineItemSuccess,
  updateLineItemFailure,
  GET_LOCATIONS,
  getLocationsSuccess,
  getLocationsFailure,
  GET_GOALS,
  getGoalsSuccess,
  getGoalsFailure,
  GET_PIXEL_STATS,
  getPixelStatsSuccess,
  getPixelStatsFailure,
  DUPLICATE_LINE_ITEM,
  duplicateLineItemSuccess,
  duplicateLineItemFailure,
  GET_LINE_ITEM_CRITERIONS,
  getLineItemCriterionsSuccess,
  getLineItemCriterionsFailure,
  CREATE_LINE_ITEM_CRITERIONS,
  createLineItemCriterionsSuccess,
  createLineItemCriterionsFailure,
  deleteLineItemCriterions,
  UPDATE_TIKTOK_LINE_ITEM_BUDGET,
  updateTikTokLineItemBudgetSuccess,
  updateTikTokLineItemBudgetFailure
} from '../actions/lineItems'

import {
  getLineItemService,
  getLineItemsService,
  createLineItemCriterionsService,
  createLineItemService,
  deleteLineItemService,
  updateLineItemService,
  getLocationsService,
  getGoalsService,
  getPixelStatsService,
  duplicateLineItemService,
  getLineItemCriterionsService,
  updateTikTokLineItemBudgetService
} from '../services/lineItems'
import {
  deleteCombinedLineItem,
  getCombinedLineItems,
  UPDATE_COMBINED_LINE_ITEM,
  updateCombinedCampaign,
  updateCombinedLineItem
} from '../actions/combinedData'
import { updateUser } from '../actions/user'
import { rightSelector } from '../selectors/rights'
import { getCurrentUserIDSelector } from '../selectors/app'
import { combinedCampaignsSelector } from '../selectors/combinedData'
import { adsSelector } from '../selectors/ads'
import { updateAdsList } from '../actions/ads'

import { groupCampaignsByProvider } from '../../pages/RightsSummary/DigitalRight/RightCampaigns/helpers'
import { formatDateBE } from '../../constants/dates'

import { FACEBOOK_PLATFORM, PLATFORMS_TO_PROVIDER, TIKTOK_PLATFORM } from '../../constants/selectLists/platformList'
import { BUDGET } from '../../forms/Tiktok/LineItemForms/LineItemTikTokEdit/fields'

function* lineItemsWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(GET_LINE_ITEM, getLineItemWorker),
    takeEvery(GET_LINE_ITEMS, getLineItemsWorker),
    takeEvery(CREATE_LINE_ITEM, createLineItemWorker),
    takeEvery(DELETE_LINE_ITEM, deleteLineItemWorker),
    takeEvery(UPDATE_LINE_ITEM, updateLineItemWorker),
    takeEvery(GET_LINE_ITEM_CRITERIONS, getLineItemCriterionsWorker),
    takeEvery(CREATE_LINE_ITEM_CRITERIONS, createLineItemCriterionsWorker),
    takeEvery(GET_LOCATIONS, getLocationsWorker),
    takeEvery(GET_GOALS, getGoalsWorker),
    takeEvery(GET_PIXEL_STATS, getPixelStatsWorker),
    takeEvery(DUPLICATE_LINE_ITEM, duplicateLineItemWorker),
    takeEvery(UPDATE_TIKTOK_LINE_ITEM_BUDGET, updateTikTokLineItemBudgetWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER
function* getLineItemWorker({ params, platform }) {
  try {
    const response = yield call(getLineItemService, platform, params)
    yield put(getLineItemSuccess(response))
  } catch (e) {
    yield put(getLineItemFailure(e))
  }
}

function* getLineItemsWorker({ params, platform }) {
  try {
    const { response } = yield race({
      response: call(getLineItemsService, platform, params),
      // need to cancel previous task, if user change the page, but task still in progress
      cancel: take(CLEAR_LINE_ITEMS)
    })

    if (response) {
      yield put(getLineItemsSuccess(response))
    }
  } catch (e) {
    yield put(getLineItemsFailure(e))
  }
}

function* createLineItemWorker({ lineItemData, platform, updatedSettings }) {
  try {
    const response = yield call(createLineItemService, lineItemData, platform)

    // enhance response with missing provider field
    const enhancedResponse = {
      ...response,
      provider: PLATFORMS_TO_PROVIDER[platform]
    }

    yield put(createLineItemSuccess(enhancedResponse))

    if (updatedSettings) {
      const userId = yield select(getCurrentUserIDSelector)
      yield put(updateUser(updatedSettings, userId))
    }

    const combinedCampaignsData = yield select(combinedCampaignsSelector)
    const { campaigns: combinedCampaigns = [] } = combinedCampaignsData
    if (combinedCampaigns.length) {
      yield call(updateCombinedCampaignsAndLineItems, {
        lineItemData,
        combinedCampaigns,
        platform
      })
    }
  } catch (e) {
    yield put(createLineItemFailure(e))
  }
}

function* deleteLineItemWorker({ params, sagaActions }) {
  try {
    yield call(deleteLineItemService, params)

    if (sagaActions?.updateAds) {
      const ads = yield select(adsSelector)
      const deleteLineItemId = params.id
      const updatedAds = ads.filter(ad => ad.line_item_id !== deleteLineItemId)

      yield put(updateAdsList(updatedAds))
    }
    if (sagaActions?.updateCombinedLineItems) {
      yield put(deleteCombinedLineItem(params.id))
    }

    yield put(deleteLineItemSuccess(params.id))
  } catch (e) {
    yield put(deleteLineItemFailure(e))
  }
}

function* updateLineItemWorker({ lineItemData, id, platform, updateOptions }) {
  try {
    const { requestMethod } = updateOptions

    const response = yield call(updateLineItemService, lineItemData, id, platform, requestMethod)

    // frequency field is returned in another format, so we just ignore it (+ it's not mutable)
    delete response.frequency

    yield put(updateLineItemSuccess(response))

    const { updateType } = updateOptions

    // Sometimes we need to update data inside combined line items store
    if (updateType === UPDATE_COMBINED_LINE_ITEM) {
      yield put(updateCombinedLineItem(response, platform))
    }
  } catch (e) {
    yield put(updateLineItemFailure(e))
  }
}

function* updateTikTokLineItemBudgetWorker({ data }) {
  try {
    yield call(updateTikTokLineItemBudgetService, data)

    const formattedData = {
      [BUDGET]: data[BUDGET][0][BUDGET],
      id: data[BUDGET][0].adgroup_id
    }

    yield put(updateTikTokLineItemBudgetSuccess(formattedData))
    yield put(updateCombinedLineItem(formattedData, TIKTOK_PLATFORM))
  } catch (e) {
    yield put(updateTikTokLineItemBudgetFailure(e))
  }
}

function* duplicateLineItemWorker({ duplicateData, id, platform }) {
  try {
    const response = yield call(duplicateLineItemService, duplicateData, id, platform)
    yield put(duplicateLineItemSuccess(response))
  } catch (e) {
    yield put(duplicateLineItemFailure(e))
  }
}

function* getLineItemCriterionsWorker({ params }) {
  try {
    const response = yield call(getLineItemCriterionsService, params)
    yield put(getLineItemCriterionsSuccess(response))
  } catch (error) {
    yield put(getLineItemCriterionsFailure(error))
  }
}

function* createLineItemCriterionsWorker({ data, criterionsToDelete }) {
  try {
    const response = yield call(createLineItemCriterionsService, data)
    yield put(createLineItemCriterionsSuccess(response))

    // filter inner store of criterions when using remove_operations in create request
    if (criterionsToDelete && criterionsToDelete.length) {
      yield put(deleteLineItemCriterions(criterionsToDelete))
    }
  } catch (error) {
    yield put(createLineItemCriterionsFailure(error))
  }
}

function* getLocationsWorker() {
  try {
    const response = yield call(getLocationsService)
    yield put(getLocationsSuccess(response))
  } catch (e) {
    yield put(getLocationsFailure(e))
  }
}

function* getGoalsWorker({ objective }) {
  try {
    const response = yield call(getGoalsService, objective)
    yield put(getGoalsSuccess(response))
  } catch (e) {
    yield put(getGoalsFailure(e))
  }
}

function* getPixelStatsWorker({ id }) {
  try {
    const response = yield call(getPixelStatsService, id)
    yield put(getPixelStatsSuccess(response))
  } catch (e) {
    yield put(getPixelStatsFailure(e))
  }
}

function* updateCombinedCampaignsAndLineItems({ lineItemData, combinedCampaigns, platform }) {
  const { campaigns: rightCampaignsToLoad } = yield select(rightSelector)
  const { campaigns_date_range: campaignsDateRange } = yield select(combinedCampaignsSelector)

  // if we create lineItem right away after campaign creation - we don't have fetched campaigns_date_range
  // so we need to use date_start and date_stop from first combined campaign or lineItem dates
  const firstCombinedCampaign = combinedCampaigns[0]
  const { date_start: campaignDateStart, date_stop: campaignDateStop } = firstCombinedCampaign
  const { time_start: lineItemDateStart, time_stop: lineItemDateStop } = lineItemData

  let defaultStartDate = campaignDateStart || lineItemDateStart
  let defaultEndDate = campaignDateStop || lineItemDateStop
  if (platform === TIKTOK_PLATFORM) {
    defaultStartDate = lineItemData.schedule_start_time
    defaultEndDate = lineItemData.schedule_end_time
  }

  const newlyCreatedCampaignDateRange = {
    date_from: formatDateBE(new Date(defaultStartDate)),
    date_to: formatDateBE(new Date(defaultEndDate))
  }

  const { date_from, date_to } = campaignsDateRange || newlyCreatedCampaignDateRange
  const campaignsGroupedByProvider = groupCampaignsByProvider(rightCampaignsToLoad)

  const { campaign_id: campaignId, budget_lifetime: budgetLifetime, budget } = lineItemData

  // When we create facebook/tiktok line item with budget,
  // we need to update parent campaign's budget_lifetime_line_item field
  // if ((platform === FACEBOOK_PLATFORM || platform === TIKTOK_PLATFORM) && combinedCampaigns.length && budgetLifetime) {
  if (
    ((platform === FACEBOOK_PLATFORM && budgetLifetime) || (platform === TIKTOK_PLATFORM && budget)) &&
    combinedCampaigns.length
  ) {
    const parentCampaign = combinedCampaigns.find(item => item.id === campaignId)

    const lineItemBudgetLifetime = platform === TIKTOK_PLATFORM ? budget : budgetLifetime

    const prevBudgetLifetimeItem = parentCampaign.budget_lifetime_line_item || 0
    const updatedBudgetLifetimeItem = Number(prevBudgetLifetimeItem) + Number(lineItemBudgetLifetime)

    const updatedParentCampaign = {
      ...parentCampaign,
      budget_lifetime_line_item: updatedBudgetLifetimeItem
    }

    yield put(updateCombinedCampaign(updatedParentCampaign))

    // We re-fetch combined line items to have actual combined line items data after new line item creation
    // On right summary page we have edit line items budgets form, so we need to have actual data
    yield put(
      getCombinedLineItems({
        insights: ['impressions', 'cpm', 'clicks', 'ctr', 'conversions'],
        date_from,
        date_to,
        providers: campaignsGroupedByProvider
      })
    )
  }
}

export default lineItemsWatcher
